import { useEffect } from "react";
import { useDebug, usePubSub } from "@huub-web/ui";
import { isArray } from "@huub-web/utils";

type InputUpdateTransform = (
  fieldName: string,
  value: unknown,
) => { fieldName: string; value: unknown };

/**
 * Subscribes to `inputUpdate` PubSub events scoped to a given prefix and
 * forwards matched updates to a callback. Extracts the repeated
 * prefix-matching logic used across template components.
 *
 * @todo Adopt this hook in other templates that handle inputUpdate
 * (e.g. FormTemplate, FormFlowTemplate, DocumentsUploadTemplate, ToggleInputTemplate, FormInputTemplate).
 */
export const useInputUpdate = ({
  prefix,
  debugLabel,
  onUpdate,
  transform,
}: {
  /**
   * Only updates whose `id` starts with this prefix are processed.
   * The prefix is stripped before the field name is forwarded.
   */
  prefix: string;
  /**
   * Label used in debug logging (e.g. the template name).
   */
  debugLabel: string;
  /**
   * Called with the collected updates object when at least one field matched.
   */
  onUpdate: (updates: Record<string, unknown>) => void;
  /**
   * Optional per-field transform applied before collecting.
   * Useful for coercing string values to booleans, numbers, etc.
   */
  transform?: InputUpdateTransform;
}) => {
  const pubSub = usePubSub();
  const { log } = useDebug();

  useEffect(() => {
    const unsubscribe = pubSub.subscribe("inputUpdate", (payload) => {
      if (!payload) return;

      const updates = isArray(payload) ? payload : [payload];
      const collected: Record<string, unknown> = {};

      for (const update of updates) {
        if (!update?.id?.startsWith(prefix)) continue;

        let fieldName = update.id.slice(prefix.length);
        let value: unknown = update.value;

        if (transform) {
          const transformed = transform(fieldName, value);
          fieldName = transformed.fieldName;
          value = transformed.value;
        }

        collected[fieldName] = value;
      }

      if (Object.keys(collected).length > 0) {
        log("purple", [debugLabel, "inputUpdate"], collected);
        onUpdate(collected);
      }
    });

    return unsubscribe;
  }, [prefix, pubSub, log, debugLabel, onUpdate, transform]);
};
